/**
 * Browser-side image downscaling (ADR 0025). There is **no server-side image processing** —
 * the legacy service resized with Jimp on Node, which cannot run on Workers — so the display
 * copy is produced here, in a canvas, before the bytes are ever uploaded.
 *
 * One size, not two: `vehicle_media` holds a single object per row, so a separate thumbnail
 * has nowhere to live (ADR 0025 correction). Cards and lists size the display copy with CSS.
 */

/** Long-edge target for the stored display copy. */
export const DISPLAY_MAX_EDGE = 1280;

/** What the caller uploads, plus the dimensions the server cannot measure for itself. */
export interface PreparedImage {
  blob: Blob;
  contentType: string;
  width: number;
  height: number;
}

/** Types we can hand to a canvas AND the API will accept (ADR 0025 — we never re-encode). */
const ENCODABLE = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Downscale to {@link DISPLAY_MAX_EDGE} on the long edge, preserving aspect ratio. An image
 * already within the limit is returned untouched rather than re-encoded — a needless
 * generation loss, and it would silently strip a format we can pass through unharmed.
 *
 * Rejects rather than uploading what the browser could not decode: better a message at
 * selection time than a 415 after a slow upload of a file we already knew was wrong.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  const type = file.type.toLowerCase();
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('decode-failed');
  });

  const longEdge = Math.max(bitmap.width, bitmap.height);
  // AVIF decodes but usually cannot be *encoded* by canvas; pass it through when small enough
  // and reject it when it would need resizing, rather than silently writing a JPEG.
  if (longEdge <= DISPLAY_MAX_EDGE) {
    const out = { blob: file, contentType: type, width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return out;
  }
  if (!ENCODABLE.has(type)) {
    bitmap.close();
    throw new Error('too-large-unencodable');
  }

  const scale = DISPLAY_MAX_EDGE / longEdge;
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('canvas-unavailable');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // 0.85 keeps a 1280px photo comfortably inside the 10 MB cap while staying clean enough to
  // judge paintwork — the whole point of the gallery.
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85),
  );
  if (!blob) throw new Error('encode-failed');

  return {
    blob,
    contentType: blob.type || 'image/jpeg',
    width,
    height,
  };
}
